import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelReportAdminUserRequest from '../../Interfaces/Requests/Administration/AddDelReportAdminUserRequest';
import GetReportAdminUserModelsUsingReportIDRequest from '../../Interfaces/Requests/Administration/GetReportAdminUserModelsUsingReportIDRequest';
import GetReportAdminUserModelsUsingReportIDResponse from '../../Interfaces/Responses/Administration/GetReportAdminUserModelsUsingReportIDResponse';
type ReportUserProps = {
    reportId: number;
    reportName: string;
    reportGroupName: string;
    close: () => void;
};

const ReportUsers: React.FC<ReportUserProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [users, setUsers] = useState<GetReportAdminUserModelsUsingReportIDResponse[]>([]);
    const [hideUnlinkedUsers, setHideUnlinkedUsers] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetReportUsersLoading, isError: isGetReportUsersError, error: getReportUsersError, refetch: refetchReportUsers } = useQuery(
        ['report-admin-users', props.reportId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_ADMIN_USER_MODELS_USING_REPORT_ID}`;
                const requestData: GetReportAdminUserModelsUsingReportIDRequest = {
                    gru_reportid: props.reportId,
                };
                return await AxiosUtil.get<GetReportAdminUserModelsUsingReportIDResponse[], GetReportAdminUserModelsUsingReportIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_REPORT_ADMIN_USER_MODELS_USING_REPORT_ID: [Get Report Users] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_USER_MODELS_USING_REPORT_ID [Get Report Users] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.reportId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_REPORT_ADMIN_USER_MODELS_USING_REPORT_ID: [Get Report Users] Internal Error: ' + err);
                console.error('GET_REPORT_ADMIN_USER_MODELS_USING_REPORT_ID [Get Report Users] Internal Error: ', err);
            },
            onSuccess: (response: GetReportAdminUserModelsUsingReportIDResponse[]) => {
                if (response) {
                    setUsers(response);
                }
            },
        }
    );

    const { mutate: addRemoveUser, isLoading: isAddRemoveUserLoading, isError: isAddRemoveUserError, error: getAddRemoveUserError } = useMutation(
        async ({ add, user, }: {
            add: boolean;
            user: GetReportAdminUserModelsUsingReportIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_REPORT_ADMIN_USERS}`;
                const addDelReportAdminUsers: AddDelReportAdminUserRequest = {
                    id: user.id,
                    userName: user.userName,
                    reportId: props.reportId,
                    reportName: props.reportName,
                    reportGroupName: props.reportGroupName,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelReportAdminUserRequest[] = [ addDelReportAdminUsers, ];
                responseData = await AxiosUtil.post<number, AddDelReportAdminUserRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_REPORT_ADMIN_USERS: [Add/Remove Report User Link] Internal Error: ' + err);
                console.error('ADD_DEL_REPORT_ADMIN_USERS [Add/Remove Report User Link] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, user };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_REPORT_ADMIN_USERS: [Add/Remove Report User Link] Internal Error: ' + err);
                console.error('ADD_DEL_REPORT_ADMIN_USERS [Add/Remove Report User Link] Internal Error: ', err);
            },
            onSuccess: ({ response, add, user }) => {
                if (response) {
                    setUsers(updateLinkedAdminRow(users, user.id, add));
                    void refetchReportUsers();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, user: GetReportAdminUserModelsUsingReportIDResponse) => {
        const params = { add: checked, user: user, };
        addRemoveUser(params);
    };

    const columns: ColumnsType<GetReportAdminUserModelsUsingReportIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetReportAdminUserModelsUsingReportIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'User', dataIndex: 'userName', key: 'userName' },
    ];

    const visibleUsers = filterLinkedAdminRows(
        users,
        hideUnlinkedUsers,
        searchText.trim(),
        (user, normalizedSearchText) => normalizedSearchText === ''
            || user.firstName.toLowerCase().includes(normalizedSearchText)
            || user.lastName.toLowerCase().includes(normalizedSearchText)
            || user.email.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowEnabledUsers = () => {
        setHideUnlinkedUsers(!hideUnlinkedUsers);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetReportUsersError,
            getReportUsersError,
            isAddRemoveUserError,
            getAddRemoveUserError
        );
    };

    return (
        <>
            <Spin spinning={isGetReportUsersLoading || isAddRemoveUserLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetReportUsersError ||
                        isAddRemoveUserError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Links to Users</Title>
                    </Text>
                </Space>
                <div style={{ textAlign: 'center' }}>
                    <Text type='secondary' italic>
                        Linking a user to a report also ensures the required tenant and dataset security links exist for that report.
                    </Text>
                </div>
                <div style={{ padding: '4px' }} />
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowEnabledUsers}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedUsers ? 'Show All Users' : 'Hide Unlinked Users'}
                            </Button>
                        </Col>
                        <Col span={4} />
                        <Col span={8}>
                            <Input
                                value={searchText}
                                allowClear={true}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={2} />
                        <Col span={6}><Text type='secondary' italic ellipsis>{'[' + props.reportGroupName + ']:' + props.reportName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetReportUsersLoading && users.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleUsers} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default ReportUsers;
